import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { VerificationDoc, ScannedTTR } from './types';
import { recordIwi } from '../../services/iwi.service';

// Optional, mirror-populated-later fields, read defensively (no change to the
// shared mirror types / resolver / plumbing). If the mirror ever exposes an
// availability link for an item, the "Available now" block lights up.
type DisplayExtras = { availabilityUrl?: string | null; availabilityLabel?: string | null };

interface Props {
  doc: VerificationDoc;
  scanned: ScannedTTR;
}

const emailLooksValid = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

/**
 * Post-verification actions (replaces the old board CTA):
 *   1. "Available now" — shown only when the item exposes an availability link
 *      (defensive: absent in the mirror today → renders nothing).
 *   2. "I Want It" interest section:
 *        - logged-in  → hand off to the IWI system (STUBBED; see handleIwi TODO).
 *        - anonymous → Collector Club lead-capture form. Writes an interestLead
 *          keyed to the item + Club (the medallion) straight to Firestore
 *          (create-only rule, mirrors scanEvents). No auth required.
 *
 * Does NOT touch the parser / resolver / mirror plumbing.
 */
const VerifyCTA: React.FC<Props> = ({ doc, scanned }) => {
  const { currentUser } = useAuth();

  const club = doc.ownership.medallionName || doc.ownership.medallionId || null;
  const itemName = doc.display.positionLabel || doc.display.boardName || scanned.ttrCode;
  const { availabilityUrl, availabilityLabel } = doc.display as typeof doc.display & DisplayExtras;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !emailLooksValid(email)) {
      toast.error('Please enter your name and a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'interestLeads'), {
        source: 'sku-verify',
        ttrCode: scanned.ttrCode,
        boardId: scanned.boardId,
        position: scanned.position,
        itemName,
        club, // keyed to the Collector Club (the medallion)
        name: name.trim(),
        email: email.trim(),
        message: message.trim() || null,
        createdAt: serverTimestamp(),
      });
      setSent(true);
    } catch (err) {
      toast.error('Something went wrong sending your interest. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Signed-in IWI. This used to toast "we noted your intent to follow up" while
  // noting nothing — a live page telling customers something untrue. It now
  // records a real iwiEvent through the same service the past-auction lots use.
  //
  // Phase 0 sends nothing to anyone, so the copy below says exactly that. The
  // verify page knows a TTR code and no sale, so auction/lot are null and the
  // TTR code is the item key — the identity that outlives any one sale.
  const [iwiSubmitting, setIwiSubmitting] = useState(false);
  const [iwiRecorded, setIwiRecorded] = useState(false);

  const handleIwi = async () => {
    setIwiSubmitting(true);
    const res = await recordIwi({
      item: {
        ttrCode: scanned.ttrCode,
        auctionId: null,
        lotId: null,
        lotNumber: null,
        sku: null,
        itemTitle: itemName,
      },
      kind: 'ping',
      requesterUid: currentUser?.uid || null,
      source: 'sku-verify',
    });
    setIwiSubmitting(false);
    if (!res.ok) {
      // Never swallowed: we are about to tell them it was recorded.
      toast.error('We could not record that just now. Nothing was saved — please try again.');
      return;
    }
    setIwiRecorded(true);
  };

  return (
    <div className="border-x border-b border-gray-200 rounded-b-lg bg-white overflow-hidden">
      {/* 1. Available now — only when the item exposes an availability link */}
      {availabilityUrl && (
        <a
          href={availabilityUrl}
          className="block px-5 py-4 bg-green-50 border-b border-green-200 hover:bg-green-100 transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-800">
            <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
            Available now
          </span>
          <p className="text-sm text-green-900 mt-1">
            {availabilityLabel || `${itemName} is available — view it now`}
          </p>
        </a>
      )}

      {/* 2. I Want It */}
      <div className="px-5 py-6 bg-gray-900 text-white">
        <h2 className="text-lg font-bold">I Want It</h2>

        {currentUser ? (
          iwiRecorded ? (
            <p className="mt-3 text-sm text-gray-100">
              Recorded against <span className="font-semibold">{itemName}</span>.{' '}
              <strong>Nothing has been sent to anyone</strong> — we are collecting interest first
              and building owner contact afterwards. We&rsquo;ll email you either way.
            </p>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-200">
                Tell us you want <span className="font-semibold">{itemName}</span>
                {club ? <> from {club}</> : null} and we&rsquo;ll record it against the item.{' '}
                <strong>Nothing is sent to anyone yet.</strong>
              </p>
              <button
                onClick={handleIwi}
                disabled={iwiSubmitting}
                className="flex sm:inline-flex items-center justify-center mt-4 min-h-[48px] w-full sm:w-auto rounded-md bg-white text-gray-900 font-semibold text-sm px-6 py-3 hover:bg-gray-100 transition-colors disabled:opacity-60"
              >
                {iwiSubmitting ? 'Recording\u2026' : 'I Want It'}
              </button>
            </>
          )
        ) : sent ? (
          <p className="mt-3 text-sm text-gray-100">
            Thanks — your interest in <span className="font-semibold">{itemName}</span> is in with{' '}
            {club || 'the Club'}. We&rsquo;ll be in touch.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-gray-200">
              Interested in <span className="font-semibold">{itemName}</span>
              {club ? <> from {club}</> : null}? Leave your details and the Club will follow up.
            </p>
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                className="w-full min-h-[44px] rounded-md px-3 py-2 text-gray-900 text-sm"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                className="w-full min-h-[44px] rounded-md px-3 py-2 text-gray-900 text-sm"
              />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message (optional)"
                rows={3}
                className="w-full rounded-md px-3 py-2 text-gray-900 text-sm"
              />
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center min-h-[48px] w-full rounded-md bg-white text-gray-900 font-semibold text-sm px-6 py-3 hover:bg-gray-100 transition-colors disabled:opacity-60"
              >
                {submitting ? 'Sending…' : 'I Want It'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyCTA;
